import React, { useState, useEffect } from 'react';
import { useTranslation } from '../i18n';
import LangToggle from './LangToggle';
import { checkReferralCode } from './api';

const SymbolWhite = ({ size = 28 }) => (
  <svg width={size} height={size} viewBox="0 0 80 80" fill="none" aria-hidden="true">
    <polyline points="16,64 16,20 40,46 64,20 64,64" stroke="#ffffff" strokeWidth="6.5" strokeLinecap="round" strokeLinejoin="round" fill="none" strokeDasharray="0.1 11" opacity="0.8"/>
    <circle cx="16" cy="64" r="5" fill="#ffffff" opacity="0.8"/>
    <circle cx="40" cy="46" r="4" fill="#ffffff" opacity="0.8"/>
    <circle cx="64" cy="64" r="4" fill="#ffffff" opacity="0.8"/>
    <path d="M55.9,20.5 L64,35.35 L72.1,20.5 Z" fill="#ffffff"/>
    <circle cx="64" cy="16" r="9" fill="#ffffff"/>
  </svg>
);

const SymbolGreen = ({ size = 26 }) => (
  <svg width={size} height={size} viewBox="0 0 80 80" fill="none" aria-hidden="true">
    <polyline points="16,64 16,20 40,46 64,20 64,64" stroke="#1f9d55" strokeWidth="6.5" strokeLinecap="round" strokeLinejoin="round" fill="none" strokeDasharray="0.1 11" opacity="0.55"/>
    <circle cx="16" cy="64" r="5" fill="#1f9d55" opacity="0.55"/>
    <circle cx="40" cy="46" r="4" fill="#1f9d55" opacity="0.55"/>
    <circle cx="64" cy="64" r="4" fill="#1f9d55" opacity="0.55"/>
    <path d="M55.9,20.5 L64,35.35 L72.1,20.5 Z" fill="#1f9d55"/>
    <circle cx="64" cy="16" r="9" fill="#1f9d55"/>
    <circle cx="64" cy="16" r="3.6" fill="#ffffff"/>
  </svg>
);

const responsiveCSS = `
  @media (max-width: 899px) {
    .ms-login-grid { grid-template-columns: 1fr !important; max-width: 460px !important; min-height: 0 !important; }
    .ms-login-left { display: none !important; }
    .ms-login-mobile-brand { display: flex !important; }
    .ms-login-mobile-lang { display: inline-flex !important; }
    .ms-login-right { padding: 36px 28px !important; }
  }
  .ms-input:focus { border-color: #1f9d55 !important; background: #fff !important; }
`;

// Self-signup is PAUSED during build-out. Flip to true to reopen the in-app
// "Start a free trial" flow. (The marketing-site CTAs are gated separately.)
const SIGNUPS_OPEN = false;

export default function Login({ onLogin }) {
  const { t } = useTranslation();
  const [mode, setMode] = useState('login');
  const [form, setForm] = useState({ email: '', password: '', businessName: '' });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [referral, setReferral] = useState(null); // { code, partner_name } once validated
  const [awaitingConfirm, setAwaitingConfirm] = useState(null); // email, once a verify link is sent
  const [confirmedBanner, setConfirmedBanner] = useState(false); // arrived back via ?confirmed=1
  const [forgotSent, setForgotSent] = useState(null); // email, once a reset link is requested

  // Returning from the email-confirmation link (?confirmed=1) — acknowledge it
  // and drop them on the sign-in form.
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    if (params.get('confirmed') === '1') {
      setConfirmedBanner(true);
      setMode('login');
    }
  }, []);

  // If the user landed via a QR-card URL (?ref=BOBSMOWER), validate the code
  // and flip into register mode so they don't have to find the toggle.
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const ref = params.get('ref');
    if (!ref || !SIGNUPS_OPEN) return;
    let cancelled = false;
    setMode('register');
    checkReferralCode(ref)
      .then((res) => {
        if (cancelled) return;
        if (res.data && res.data.valid) {
          setReferral({ code: res.data.code, partner_name: res.data.partner_name });
        }
      })
      .catch(() => { /* ignore — bad codes are silently dropped */ });
    return () => { cancelled = true; };
  }, []);

  const handleChange = (e) => {
    setForm({ ...form, [e.target.name]: e.target.value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    try {
      const endpoint = mode === 'login' ? '/auth/login' : '/auth/register';
      const body = mode === 'login'
        ? { email: form.email, password: form.password }
        : {
            email: form.email,
            password: form.password,
            businessName: form.businessName,
            ...(referral ? { referral_code: referral.code } : {})
          };

      const res = await fetch(`${process.env.REACT_APP_API_URL}${endpoint}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body)
      });

      const data = await res.json();
      if (!res.ok) {
        setError(data.error || t('login.error_generic'));
        setLoading(false);
        return;
      }

      // After registration, log the user in automatically so they land in
      // the app — App.js will route them to the Billing screen to add a card.
      if (mode === 'register') {
        // If email verification is enabled server-side, the account isn't
        // active until they click the link, so we can't sign them in yet.
        if (data.needs_confirmation) {
          setAwaitingConfirm(form.email);
          setLoading(false);
          return;
        }
        const loginRes = await fetch(`${process.env.REACT_APP_API_URL}/auth/login`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ email: form.email, password: form.password })
        });
        const loginData = await loginRes.json();
        if (!loginRes.ok) {
          setError(loginData.error || t('login.account_created_signin'));
          setMode('login');
          setForm({ email: form.email, password: '', businessName: '' });
          setLoading(false);
          return;
        }
        localStorage.setItem('token', loginData.token);
        localStorage.setItem('business', JSON.stringify(loginData.business));
        onLogin(loginData.business, loginData.token);
        return;
      }

      localStorage.setItem('token', data.token);
      localStorage.setItem('business', JSON.stringify(data.business));
      onLogin(data.business, data.token);
    } catch {
      setError(t('login.error_connect'));
    }
    setLoading(false);
  };

  const handleForgot = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    try {
      const res = await fetch(`${process.env.REACT_APP_API_URL}/auth/forgot-password`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email: form.email })
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) { setError(data.error || t('login.error_generic')); setLoading(false); return; }
      setForgotSent(form.email);
    } catch {
      setError(t('login.error_connect'));
    }
    setLoading(false);
  };

  const isRegister = mode === 'register';
  const isForgot = mode === 'forgot';

  return (
    <>
      <style>{responsiveCSS}</style>
      <div style={styles.page}>
        <div className="ms-login-grid" style={styles.grid}>
          {/* Left: brand panel */}
          <div className="ms-login-left" style={styles.left}>
            <div style={styles.stripes} />
            <div style={styles.topRow}>
              <div style={styles.brand}>
                <SymbolWhite size={28} />
                <span style={styles.brandText}>MowStop</span>
              </div>
              <LangToggle variant="dark" />
            </div>
            <h1 style={styles.headline}>{t('login.headline')}</h1>
            <div style={styles.leftFooter}>{t('login.footer')}</div>
          </div>

          {/* Right: form panel */}
          <div className="ms-login-right" style={styles.right}>
            <div className="ms-login-mobile-brand" style={styles.mobileBrand}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 9 }}>
                <SymbolGreen size={26} />
                <span style={styles.mobileBrandText}>
                  <span style={{ color: '#14653a' }}>Mow</span>
                  <span style={{ color: '#1f9d55' }}>Stop</span>
                </span>
              </div>
              <LangToggle className="ms-login-mobile-lang" />
            </div>

            <div style={styles.formWrap}>
              {awaitingConfirm ? (
                <div>
                  <h2 style={styles.formTitle}>{t('login.check_email_title')}</h2>
                  <p style={styles.formSub}>{t('login.check_email_body', { email: awaitingConfirm })}</p>
                  <div role="status" aria-live="polite" style={styles.referralBadge}>
                    <span style={styles.referralCheck} aria-hidden="true">✉</span>
                    <span>{t('login.check_email_hint')}</span>
                  </div>
                  <button
                    type="button"
                    style={styles.submitBtn}
                    onClick={() => {
                      const email = awaitingConfirm;
                      setAwaitingConfirm(null);
                      setMode('login');
                      setForm({ email, password: '', businessName: '' });
                    }}
                  >
                    {t('login.back_to_signin')}
                  </button>
                </div>
              ) : isForgot ? (
                <div>
                  {forgotSent ? (
                    <>
                      <h2 style={styles.formTitle}>{t('login.check_email_title')}</h2>
                      <p style={styles.formSub}>{t('login.forgot_sent_body', { email: forgotSent })}</p>
                      <div role="status" aria-live="polite" style={styles.referralBadge}>
                        <span style={styles.referralCheck} aria-hidden="true">✉</span>
                        <span>{t('login.check_email_hint')}</span>
                      </div>
                      <button type="button" style={styles.submitBtn} onClick={() => { setForgotSent(null); setMode('login'); setError(''); }}>
                        {t('login.back_to_signin')}
                      </button>
                    </>
                  ) : (
                    <>
                      <h2 style={styles.formTitle}>{t('login.forgot_title')}</h2>
                      <p style={styles.formSub}>{t('login.forgot_sub')}</p>
                      {error && <div role="alert" style={styles.errorBox}>{error}</div>}
                      <form onSubmit={handleForgot}>
                        <div style={styles.field}>
                          <label htmlFor="forgot-email" style={styles.label}>{t('login.work_email')}</label>
                          <input
                            className="ms-input"
                            style={styles.input}
                            id="forgot-email"
                            name="email"
                            type="email"
                            placeholder={t('login.work_email_placeholder')}
                            value={form.email}
                            onChange={handleChange}
                            required
                            autoComplete="email"
                          />
                        </div>
                        <button style={styles.submitBtn} type="submit" disabled={loading}>
                          {loading ? t('login.please_wait') : t('login.send_reset_link')}
                        </button>
                      </form>
                      <div style={styles.switchRow}>
                        <button type="button" style={styles.switchLink} onClick={() => { setMode('login'); setError(''); }}>
                          {t('login.back_to_signin')}
                        </button>
                      </div>
                    </>
                  )}
                </div>
              ) : (
                <>
              <h2 style={styles.formTitle}>
                {isRegister ? t('login.create_account_title') : t('login.welcome_back')}
              </h2>
              <p style={styles.formSub}>
                {isRegister ? t('login.sub_register') : t('login.sub_login')}
              </p>

              {confirmedBanner && (
                <div role="status" aria-live="polite" style={styles.referralBadge}>
                  <span style={styles.referralCheck} aria-hidden="true">✓</span>
                  <span>{t('login.confirmed_banner')}</span>
                </div>
              )}

              {error && <div role="alert" style={styles.errorBox}>{error}</div>}

              {isRegister && referral && (
                <div role="status" aria-live="polite" style={styles.referralBadge}>
                  <span style={styles.referralCheck} aria-hidden="true">✓</span>
                  <span>
                    {t('login.referred_by')} <b>{referral.partner_name}</b>
                  </span>
                </div>
              )}

              <form onSubmit={handleSubmit}>
                {isRegister && (
                  <div style={styles.field}>
                    <label htmlFor="login-business-name" style={styles.label}>
                      {t('login.business_name')} <span aria-hidden="true">*</span>
                    </label>
                    <input
                      className="ms-input"
                      style={styles.input}
                      id="login-business-name"
                      name="businessName"
                      placeholder={t('login.business_name_placeholder')}
                      value={form.businessName}
                      onChange={handleChange}
                      required
                      aria-required="true"
                    />
                  </div>
                )}
                <div style={styles.field}>
                  <label htmlFor="login-email" style={styles.label}>
                    {t('login.work_email')} <span aria-hidden="true">*</span>
                  </label>
                  <input
                    className="ms-input"
                    style={styles.input}
                    id="login-email"
                    name="email"
                    type="email"
                    placeholder={t('login.work_email_placeholder')}
                    value={form.email}
                    onChange={handleChange}
                    required
                    aria-required="true"
                    autoComplete="email"
                  />
                </div>
                <div style={styles.field}>
                  <label htmlFor="login-password" style={styles.label}>
                    {t('login.password')} <span aria-hidden="true">*</span>
                  </label>
                  <input
                    className="ms-input"
                    style={styles.input}
                    id="login-password"
                    name="password"
                    type="password"
                    placeholder={t('login.password_placeholder')}
                    value={form.password}
                    onChange={handleChange}
                    required
                    aria-required="true"
                    autoComplete={isRegister ? 'new-password' : 'current-password'}
                  />
                </div>
                <button style={styles.submitBtn} type="submit" disabled={loading}>
                  {loading
                    ? t('login.please_wait')
                    : isRegister
                      ? t('login.create_account')
                      : t('login.sign_in')}
                </button>
              </form>

              <div style={styles.switchRow}>
                {isRegister ? (
                  <>
                    {t('login.already_have_account')}{' '}
                    <button
                      type="button"
                      style={styles.switchLink}
                      onClick={() => { setMode('login'); setError(''); }}
                    >
                      {t('login.sign_in_link')}
                    </button>
                  </>
                ) : (
                  <>
                    <button
                      type="button"
                      style={styles.switchLink}
                      onClick={() => { setMode('forgot'); setError(''); }}
                    >
                      {t('login.forgot_link')}
                    </button>
                    {SIGNUPS_OPEN && (
                      <>
                        {' · '}{t('login.new_crew_prefix')}{' '}
                        <button
                          type="button"
                          style={styles.switchLink}
                          onClick={() => { setMode('register'); setError(''); }}
                        >
                          {t('login.start_free_trial_link')}
                        </button>
                      </>
                    )}
                  </>
                )}
              </div>
                </>
              )}
            </div>
          </div>
        </div>
      </div>
    </>
  );
}

const styles = {
  page: {
    minHeight: '100vh',
    background: '#f3f4ef',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    padding: 24,
    fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif'
  },
  grid: {
    display: 'grid',
    gridTemplateColumns: '1fr 1fr',
    width: '100%',
    maxWidth: 1100,
    minHeight: 580,
    background: '#fff',
    borderRadius: 22,
    overflow: 'hidden',
    boxShadow: '0 30px 80px rgba(20, 101, 58, 0.10), 0 4px 16px rgba(0,0,0,0.04)'
  },
  left: {
    background: '#1f9d55',
    position: 'relative',
    overflow: 'hidden',
    padding: '40px 44px',
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'space-between',
    color: '#fff',
    minHeight: 580
  },
  stripes: {
    position: 'absolute',
    inset: 0,
    background: 'repeating-linear-gradient(135deg, transparent 0px, transparent 28px, rgba(255,255,255,0.06) 28px, rgba(255,255,255,0.06) 60px)',
    pointerEvents: 'none'
  },
  topRow: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    position: 'relative',
    zIndex: 1
  },
  brand: {
    display: 'flex',
    alignItems: 'center',
    gap: 10
  },
  brandText: {
    fontSize: 22,
    fontWeight: 800,
    letterSpacing: '-0.5px',
    fontFamily: 'Manrope, -apple-system, BlinkMacSystemFont, sans-serif',
    color: '#fff'
  },
  headline: {
    fontSize: 30,
    fontWeight: 800,
    letterSpacing: '-0.8px',
    lineHeight: 1.18,
    position: 'relative',
    zIndex: 1,
    maxWidth: 360,
    fontFamily: 'Manrope, -apple-system, BlinkMacSystemFont, sans-serif',
    color: '#fff'
  },
  leftFooter: {
    fontSize: 12,
    color: 'rgba(255,255,255,0.75)',
    position: 'relative',
    zIndex: 1
  },
  right: {
    padding: '52px 56px',
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'center'
  },
  mobileBrand: {
    display: 'none',
    alignItems: 'center',
    justifyContent: 'space-between',
    marginBottom: 26
  },
  mobileBrandText: {
    fontSize: 20,
    fontWeight: 800,
    letterSpacing: '-0.5px',
    fontFamily: 'Manrope, -apple-system, BlinkMacSystemFont, sans-serif'
  },
  formWrap: {
    width: '100%',
    maxWidth: 380,
    margin: '0 auto'
  },
  formTitle: {
    fontSize: 26,
    fontWeight: 800,
    letterSpacing: '-0.5px',
    color: '#1a1a1a',
    marginBottom: 6,
    fontFamily: 'Manrope, -apple-system, BlinkMacSystemFont, sans-serif'
  },
  formSub: {
    fontSize: 14,
    color: '#5f6b58',
    marginBottom: 28,
    lineHeight: 1.5
  },
  errorBox: {
    background: '#FEF2F2',
    color: '#dc2626',
    borderRadius: 8,
    padding: '10px 12px',
    fontSize: 13,
    marginBottom: 18
  },
  field: { marginBottom: 16 },
  label: {
    display: 'block',
    fontSize: 12,
    fontWeight: 600,
    color: '#5f6b58',
    marginBottom: 8,
    letterSpacing: '0.01em'
  },
  input: {
    width: '100%',
    padding: '14px 16px',
    border: '1px solid #e3e9df',
    borderRadius: 10,
    fontSize: 15,
    color: '#1a1a1a',
    background: '#f7f9f4',
    boxSizing: 'border-box',
    outline: 'none',
    transition: 'border-color .15s, background .15s',
    fontFamily: 'inherit'
  },
  submitBtn: {
    width: '100%',
    padding: '14px',
    border: 'none',
    background: '#14653a',
    borderRadius: 10,
    fontSize: 15,
    fontWeight: 700,
    color: '#fff',
    cursor: 'pointer',
    marginTop: 6,
    fontFamily: 'Manrope, -apple-system, BlinkMacSystemFont, sans-serif',
    letterSpacing: '0.01em'
  },
  switchRow: {
    textAlign: 'center',
    marginTop: 24,
    fontSize: 13.5,
    color: '#5f6b58'
  },
  switchLink: {
    background: 'none',
    border: 'none',
    color: '#14653a',
    fontWeight: 700,
    cursor: 'pointer',
    padding: 0,
    fontSize: 13.5,
    fontFamily: 'inherit',
    textDecoration: 'underline'
  },
  referralBadge: {
    display: 'flex',
    alignItems: 'center',
    gap: 8,
    background: '#ecf8ee',
    color: '#14653a',
    border: '1px solid #cfe9d4',
    borderRadius: 10,
    padding: '10px 12px',
    fontSize: 13,
    marginBottom: 16
  },
  referralCheck: {
    width: 20,
    height: 20,
    borderRadius: '50%',
    background: '#1f9d55',
    color: '#fff',
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontSize: 12,
    fontWeight: 800,
    flexShrink: 0
  }
};
